import { Component, OnInit, Input } from '@angular/core';
import { ValueAccessorBase } from '../../shared/value-accessor/value-accessor-base';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { RequestModel } from 'src/app/models/request/request-model';

/**
 * Dashboard "data quality" card.
 * settings.ApiEndpoint (module, POST) returns { BadgeText, Items[{Label,Percentage,Color}],
 * RecommendationLabel, RecommendationText, RecommendationLinkText, RecommendationUrl }. Without an
 * endpoint the card uses the static settings values, and only as a last resort a neutral design
 * placeholder. All application wording comes from the module (endpoint or settings), never from here.
 */
@Component({
    selector: 'sem-data-quality',
    templateUrl: './sem-data-quality.component.html',
    styleUrls: ['./sem-data-quality.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: SemDataQualityComponent, multi: true }
    ],
    standalone: false
})
export class SemDataQualityComponent extends ValueAccessorBase<string> implements OnInit {

  @Input() settings: any;

  /** Design-time preview only (no endpoint, no static items configured). */
  static readonly PLACEHOLDER_ITEMS = [
    { Label: 'Metric 1', Percentage: 80, Color: '#3b82f6' },
    { Label: 'Metric 2', Percentage: 60, Color: '#22c55e' },
    { Label: 'Metric 3', Percentage: 40, Color: '#f59e0b' },
    { Label: 'Metric 4', Percentage: 20, Color: '#f87171' }
  ];

  items: any[] = [];
  badgeText = '';
  recommendationLabel = 'Recommendation';
  recommendation = { Text: '', LinkText: '', Url: '' };
  loading = false;

  ngOnInit() {
    this.applyFromSettings();

    const endpoint: string = this.settings?.ApiEndpoint;
    if (!endpoint) {
      if (!this.items.length) {
        this.items = SemDataQualityComponent.PLACEHOLDER_ITEMS;
        this.recommendation = { Text: 'Configure ApiEndpoint or the static values in the control settings.', LinkText: '', Url: '' };
      }
      return;
    }

    this.loading = true;
    const req = new RequestModel();
    req.url = endpoint;
    req.data = {};

    this.restProviderService.getDataPOST<any>(req).subscribe({
      next: (res) => {
        const p = typeof res === 'string' ? this.tryParse(res) : res;
        if (!p) { this.loading = false; return; }
        const items = this.readList(p.Items ?? p.items);
        if (items.length) this.items = items;
        this.badgeText = p.BadgeText ?? p.badgeText ?? this.badgeText;
        this.recommendationLabel = p.RecommendationLabel ?? p.recommendationLabel ?? this.recommendationLabel;
        this.recommendation = {
          Text: p.RecommendationText ?? p.recommendationText ?? this.recommendation.Text,
          LinkText: p.RecommendationLinkText ?? p.recommendationLinkText ?? this.recommendation.LinkText,
          Url: p.RecommendationUrl ?? p.recommendationUrl ?? this.recommendation.Url
        };
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  private applyFromSettings() {
    const s = this.settings;
    if (!s) return;
    const items = this.readList(s.Items);
    if (items.length) this.items = items;
    if (s.BadgeText) this.badgeText = s.BadgeText;
    if (s.RecommendationLabel) this.recommendationLabel = s.RecommendationLabel;
    if (s.RecommendationText) {
      this.recommendation = { Text: s.RecommendationText, LinkText: s.RecommendationLinkText || '', Url: s.RecommendationUrl || '' };
    }
  }

  private readList(raw: any): any[] {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : (raw.$values ?? []);
    return list.filter((i: any) => i && i.Label);
  }

  private tryParse(text: string): any {
    try { return JSON.parse(text); } catch { return null; }
  }

  navigate(url: string) {
    if (!url || url === '#') return;
    this.utility.processNavigationUrl(url);
  }

}
